import { useMemo } from "react";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import type { PendingRegistration } from "@/lib/api/registration-approval";
import { useActiveBranches } from "@/hooks/useActiveBranches";
import { RejectedRegistrationReview } from "./RejectedRegistrationReview";
import { useRejectedRegistrations } from "./useRejectedRegistrations";
import { Eye, UserX } from "lucide-react";

interface RejectedRegistrationsPanelProps {
  searchQuery: string;
}

export function RejectedRegistrationsPanel({ searchQuery }: RejectedRegistrationsPanelProps) {
  const queue = useRejectedRegistrations();
  const { data: branches = [] } = useActiveBranches();

  const filtered = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return queue.registrations;
    return queue.registrations.filter(
      (row) =>
        row.email.toLowerCase().includes(query) ||
        row.firstName?.toLowerCase().includes(query) ||
        row.lastName?.toLowerCase().includes(query)
    );
  }, [queue.registrations, searchQuery]);

  const columns: DataTableColumn<PendingRegistration>[] = [
    {
      id: "patron",
      header: "Patron",
      cell: (row) => (
        <div>
          <p className="font-medium text-slate-900">
            {[row.firstName, row.lastName].filter(Boolean).join(" ") || "—"}
          </p>
          <p className="mt-0.5 text-xs text-slate-500">{row.email}</p>
        </div>
      ),
    },
    {
      id: "reason",
      header: "Reason",
      cell: (row) => (
        <span className="text-sm text-slate-600">
          {(row.metadata?.rejectionNotes as string) || "—"}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      align: "right",
      cell: (row) => (
        <Button variant="outline" size="sm" onClick={() => void queue.openReview(row.id)}>
          <Eye className="mr-1.5 h-3.5 w-3.5" />
          Review
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-3">
      {queue.error && (
        <Alert variant="destructive">
          <AlertDescription>{queue.error}</AlertDescription>
        </Alert>
      )}
      {queue.success && (
        <Alert className="border-emerald-200 bg-emerald-50">
          <AlertDescription className="text-emerald-800">{queue.success}</AlertDescription>
        </Alert>
      )}
      <DataTable
        columns={columns}
        data={filtered}
        rowKey={(row) => row.id}
        loading={queue.loading}
        emptyState={
          <EmptyState
            icon={<UserX className="h-6 w-6 text-slate-400" />}
            title="No rejected applications"
            description="Rejected accounts stay here until someone reapplies, or staff reopen or approve them."
          />
        }
      />
      <RejectedRegistrationReview
        open={queue.open}
        details={queue.details}
        patronTypes={queue.patronTypes}
        branches={branches}
        busyAction={queue.busyAction}
        onOpenChange={queue.setOpen}
        onReopen={() => void queue.reopen()}
        onApprove={(input) => void queue.approve(input)}
      />
    </div>
  );
}
